import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { formatAppName } from '../lib/appName';
import { formatTimestamp } from '../lib/history';
import type { Deployment, DeploymentQueueItem } from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

interface DeploymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deployment: Deployment;
  isQueued: boolean;
  onEnqueueDeployment: (item: DeploymentQueueItem) => void;
  onDequeueDeployment: (appName: string, environment: string) => void;
}

export const DeploymentDialog = ({
  open,
  onOpenChange,
  deployment,
  isQueued,
  onEnqueueDeployment,
  onDequeueDeployment,
}: DeploymentDialogProps) => {
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return 'Successful';
      case 'failure':
      case 'error':
        return 'Failed';
      case 'in_progress':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const getStatusColorClass = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'failure':
      case 'error':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'in_progress':
      case 'pending':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const githubWorkflowUrl = deployment.githubWorkflowRunId
    ? `https://github.com/THEY-Consulting/cp-api/actions/runs/${deployment.githubWorkflowRunId}`
    : undefined;

  const envColorClass =
    deployment.environment === 'prod'
      ? 'bg-green-100 text-green-800'
      : deployment.environment === 'dev'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-700';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-lg'>
        <DialogHeader>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogTitle>{formatAppName(deployment.appName)}</DialogTitle>
            </div>
            <div className='flex items-center gap-1.5 flex-shrink-0'>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${envColorClass}`}>
                {deployment.environment}
              </span>
              <CopyableVersionTag version={deployment.version} />
            </div>
          </div>
        </DialogHeader>

        <div>
          <div className='space-y-3'>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Branch
              </div>
              <div className='text-sm font-mono text-gray-900 break-all'>{deployment.branch}</div>
            </div>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Status
              </div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColorClass(deployment.status)}`}
              >
                {getStatusLabel(deployment.status)}
              </span>
            </div>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Deployed by
              </div>
              <div className='text-sm text-gray-900'>{deployment.createdBy}</div>
            </div>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Deployed at
              </div>
              <div className='text-sm text-gray-900'>{formatTimestamp(deployment.createdAt)}</div>
            </div>
          </div>
        </div>

        {githubWorkflowUrl && (
          <div className='mt-4'>
            <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
              Workflow
            </div>
            <a
              href={githubWorkflowUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1.5 text-sm text-primary hover:underline'
            >
              <ArrowTopRightOnSquareIcon className='w-4 h-4' />
              Run #{deployment.githubWorkflowRunId}
            </a>
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              if (isQueued) {
                onDequeueDeployment(deployment.appName, deployment.environment);
                return;
              }

              onEnqueueDeployment({
                appName: deployment.appName,
                environment: deployment.environment,
                version: deployment.version,
              });
            }}
            className={
              isQueued ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80' : undefined
            }
            title={isQueued ? 'Cancel deployment' : undefined}
          >
            {isQueued
              ? `${deployment.environment === 'prod' ? 'Prod' : 'Dev'} Queued`
              : `Deploy ${deployment.environment === 'prod' ? 'Prod' : 'Dev'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
